/*
 * Planigo
 * Auteur  : Stéphane Raverdy
 * Société : SCIAD 2026
 */
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Check, X, ArrowCounterClockwise, CaretDown, CaretRight, Download, FilePdf } from '@phosphor-icons/react';
import { formatDate } from '../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;
const apiFetch = (url, opts = {}) => fetch(`${API}${url}`, { credentials: 'include', ...opts });
const apiJson = (url, method, body) => apiFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

const statusBadge = {
  draft: 'bg-slate-100 text-slate-700', submitted: 'bg-blue-100 text-blue-700', manager_validated: 'bg-green-100 text-green-700', closed: 'bg-emerald-100 text-emerald-800',
  pending: 'bg-amber-100 text-amber-700', approved: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-600', empty: 'bg-slate-100 text-slate-500',
};
const statusLabel = { draft:'Brouillon', submitted:'Soumis consultant', manager_validated:'Validé responsable', closed:'Clôturé', pending: 'En attente', approved: 'Validé', rejected: 'Rejeté', empty: 'Vide' };

export default function ValidationsPage() {
  const { user } = useAuth();
  const isManager = ['admin', 'supervisor', 'manager', 'project_manager'].includes(user?.role);
  const [craGroups, setCraGroups] = useState([]);
  const [expGroups, setExpGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterMonth, setFilterMonth] = useState(new Date().toISOString().slice(0, 7));
  const [filterUser, setFilterUser] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedCRA, setExpandedCRA] = useState({});
  const [expandedExp, setExpandedExp] = useState({});
  const [selectedCRA, setSelectedCRA] = useState([]); // list of `${user_id}|${month}`
  const [selectedExp, setSelectedExp] = useState([]);
  const [rejectDialog, setRejectDialog] = useState({ open: false, type: '', user_id: '', month: '', reason: '' });

  const fetchData = async () => {
    try {
      const [craRes, expRes, usrRes] = await Promise.all([
        apiFetch(`/api/time/cra-monthly?month=${filterMonth}`).then(r => r.json()),
        apiFetch(`/api/expenses/monthly?month=${filterMonth}`).then(r => r.json()),
        apiFetch('/api/admin/users').then(r => r.json()).catch(() => []),
      ]);
      setCraGroups(craRes.groups || []);
      setExpGroups(expRes.groups || []);
      setUsers(Array.isArray(usrRes) ? usrRes : []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { setLoading(true); fetchData(); /* eslint-disable-next-line */ }, [filterMonth]);

  const matchesGroup = (g) => {
    if (filterUser !== 'all' && g.user_id !== filterUser) return false;
    if (filterStatus !== 'all' && g.status !== filterStatus) return false;
    return true;
  };
  const filteredCRA = craGroups.filter(g => g.is_external_provider).filter(matchesGroup);
  const filteredExp = expGroups.filter(matchesGroup);

  const keyOf = (g) => `${g.user_id}|${g.month}`;
  const toggleCRA = (g) => setSelectedCRA(p => p.includes(keyOf(g)) ? p.filter(x => x !== keyOf(g)) : [...p, keyOf(g)]);
  const toggleExp = (g) => setSelectedExp(p => p.includes(keyOf(g)) ? p.filter(x => x !== keyOf(g)) : [...p, keyOf(g)]);
  const allCRAKeys = filteredCRA.map(keyOf);
  const allExpKeys = filteredExp.map(keyOf);
  const allCRASelected = allCRAKeys.length > 0 && allCRAKeys.every(k => selectedCRA.includes(k));
  const allExpSelected = allExpKeys.length > 0 && allExpKeys.every(k => selectedExp.includes(k));
  const toggleAllCRA = () => setSelectedCRA(allCRASelected ? [] : allCRAKeys);
  const toggleAllExp = () => setSelectedExp(allExpSelected ? [] : allExpKeys);

  const callMonthAction = async (endpoint, user_id, month, action, reason = '') => {
    return apiJson(endpoint, 'POST', { user_id, month, action, reason });
  };

  // Single month action (CRA)
  const handleCRAMonth = async (g, action) => {
    if (action === 'reject') {
      setRejectDialog({ open: true, type: 'cra', user_id: g.user_id, month: g.month, reason: '' });
      return;
    }
    try {
      await callMonthAction('/api/time/cra-monthly/manager-action', g.user_id, g.month, action);
      toast.success(action === 'validate' ? 'CRA valide par le responsable' : action === 'close' ? 'CRA cloture' : 'CRA mis a jour');
      fetchData();
    } catch(e) { toast.error('Action CRA impossible'); }
  };

  // Batch CRA action
  const handleBatchCRA = async (action) => {
    if (!selectedCRA.length) return;
    try {
      await Promise.all(selectedCRA.map(k => {
        const [uid, mo] = k.split('|');
        return callMonthAction('/api/time/cra-monthly/manager-action', uid, mo, action);
      }));
      toast.success(`${selectedCRA.length} CRA mis a jour`);
      setSelectedCRA([]); fetchData();
    } catch { toast.error('Erreur'); }
  };

  // Single month action (Expenses)
  const handleExpMonth = async (g, action) => {
    if (action === 'reject') {
      setRejectDialog({ open: true, type: 'expense', user_id: g.user_id, month: g.month, reason: '' });
      return;
    }
    try {
      await callMonthAction('/api/expenses/monthly/action', g.user_id, g.month, action);
      toast.success(action === 'approve' ? 'Notes approuvées' : 'Notes remises en attente');
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const handleBatchExp = async (action) => {
    if (!selectedExp.length) return;
    try {
      await Promise.all(selectedExp.map(k => {
        const [uid, mo] = k.split('|');
        return callMonthAction('/api/expenses/monthly/action', uid, mo, action);
      }));
      toast.success(`${selectedExp.length} groupe(s) mis a jour`);
      setSelectedExp([]); fetchData();
    } catch { toast.error('Erreur'); }
  };

  const handleReject = async () => {
    const { type, user_id, month, reason } = rejectDialog;
    try {
      const endpoint = type === 'expense' ? '/api/expenses/monthly/action' : '/api/time/cra-monthly/manager-action';
      await callMonthAction(endpoint, user_id, month, 'reject', reason);
      toast.success('Rejeté et collaborateur notifié');
      setRejectDialog({ open: false, type: '', user_id: '', month: '', reason: '' });
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const downloadExpensePdf = async (g) => {
    try {
      const res = await apiFetch(`/api/expenses/monthly/pdf?user_id=${g.user_id}&month=${g.month}`);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = `note_de_frais_R-${String(g.user_id||'').slice(-6).toUpperCase()}_${g.month}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('PDF généré');
    } catch { toast.error('Erreur PDF'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;

  const pendingCRA = craGroups.filter(g => g.is_external_provider && g.status === 'submitted').length;
  const pendingExp = expGroups.filter(g => g.status === 'pending').length;
  const [fy,fm] = filterMonth.split('-').map(Number);
  const craStart = `${filterMonth}-01`;
  const craEnd = `${fy}-${String(fm).padStart(2,'0')}-${String(new Date(fy, fm, 0).getDate()).padStart(2,'0')}`;
  const craExportUrl = (kind, uid='') => `${API}/api/time/cra/export-${kind}?start_date=${craStart}&end_date=${craEnd}${uid?`&user_id=${uid}`:''}`;

  return (
    <div className="p-5 space-y-4" data-testid="validations-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-heading font-extrabold text-slate-900">Suivi des CRA</h1>
          <p className="text-xs text-slate-500 mt-0.5">Validation des CRA des prestataires externes, suivi des temps et rapports mensuels</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={filterMonth} onValueChange={setFilterMonth}>
            <SelectTrigger className="w-40 h-8 text-xs" data-testid="filter-month"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Array.from({ length: 12 }, (_, i) => {
                const d = new Date(); d.setMonth(d.getMonth() - i);
                const v = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                return <SelectItem key={v} value={v}>{d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}</SelectItem>;
              })}
            </SelectContent>
          </Select>
          <Select value={filterUser} onValueChange={setFilterUser}>
            <SelectTrigger className="w-44 h-8 text-xs" data-testid="filter-user"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les collaborateurs</SelectItem>
              {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-36 h-8 text-xs" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              <SelectItem value="draft">Brouillon</SelectItem>
              <SelectItem value="submitted">Soumis consultant</SelectItem>
              <SelectItem value="manager_validated">Valide responsable</SelectItem>
              <SelectItem value="closed">Cloture</SelectItem>
              <SelectItem value="rejected">Rejete</SelectItem>
            </SelectContent>
          </Select>
          <a href={craExportUrl('excel')}><Button variant="outline" size="sm" className="h-8 text-xs"><Download size={13} className="mr-1"/>CRA global Excel</Button></a>
          <a href={craExportUrl('pdf')}><Button variant="outline" size="sm" className="h-8 text-xs"><FilePdf size={13} className="mr-1"/>CRA global PDF</Button></a>
        </div>
      </div>

      <Tabs defaultValue="cra" className="space-y-3">
        <TabsList className="bg-white border">
          <TabsTrigger value="cra" className="text-xs gap-1.5" data-testid="tab-cra">CRA prestataires ({pendingCRA} en attente)</TabsTrigger>
          <TabsTrigger value="expenses" className="text-xs gap-1.5" data-testid="tab-expenses">Notes de frais ({pendingExp} en attente)</TabsTrigger>
        </TabsList>

        {/* CRA TAB */}
        <TabsContent value="cra">
          {isManager && filteredCRA.length > 0 && (
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Button variant="outline" size="sm" onClick={toggleAllCRA} data-testid="cra-toggle-all">
                {allCRASelected ? 'Tout désélectionner' : `Tout sélectionner (${allCRAKeys.length})`}
              </Button>
              {selectedCRA.length > 0 && <>
                <span className="text-xs text-slate-500">{selectedCRA.length} sélectionné(s)</span>
                <Button className="btn-primary" size="sm" onClick={() => handleBatchCRA('validate')} data-testid="cra-batch-approve">
                  <Check size={14} className="mr-1" />Valider
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleBatchCRA('reopen')} className="text-amber-700 border-amber-300" data-testid="cra-batch-revert">
                  <ArrowCounterClockwise size={14} className="mr-1" />Remettre en attente
                </Button>
              </>}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
            <Card className="card-surface"><CardContent className="p-3"><div className="text-[10px] uppercase text-slate-500">Total facturable</div><div className="text-lg font-bold text-green-700">{filteredCRA.reduce((s,g)=>s+Number(g.billable_days||0),0).toFixed(2)} j</div></CardContent></Card>
            <Card className="card-surface"><CardContent className="p-3"><div className="text-[10px] uppercase text-slate-500">Total non-facturable</div><div className="text-lg font-bold text-slate-700">{filteredCRA.reduce((s,g)=>s+Number(g.non_billable_days||0),0).toFixed(2)} j</div></CardContent></Card>
            <Card className="card-surface"><CardContent className="p-3"><div className="text-[10px] uppercase text-slate-500">Total CRA prestataires</div><div className="text-lg font-bold text-blue-700">{filteredCRA.reduce((s,g)=>s+Number(g.total_days||0),0).toFixed(2)} j</div></CardContent></Card>
          </div>
          <div className="space-y-2">
            {filteredCRA.length === 0 && (
              <Card className="card-surface"><CardContent className="py-8 text-center text-sm text-slate-500">Aucun CRA pour cette période</CardContent></Card>
            )}
            {filteredCRA.map(g => {
              const k = keyOf(g);
              const expanded = expandedCRA[k];
              return (
                <Card key={k} className="card-surface" data-testid={`cra-group-${g.user_id}`}>
                  <div className="flex items-center px-3 py-2.5 hover:bg-slate-50 cursor-pointer" onClick={() => setExpandedCRA(p => ({ ...p, [k]: !p[k] }))}>
                    {isManager && (
                      <input type="checkbox" className="mr-3" checked={selectedCRA.includes(k)} onClick={e => e.stopPropagation()} onChange={() => toggleCRA(g)} data-testid={`cra-cb-${g.user_id}`} />
                    )}
                    {expanded ? <CaretDown size={14} className="text-slate-400 mr-2" /> : <CaretRight size={14} className="text-slate-400 mr-2" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-800 truncate">{g.user_name}</span>
                        <Badge className={`text-[10px] ${statusBadge[g.status]}`}>{statusLabel[g.status]}</Badge>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        {Number(g.worked_days||g.total_days||0).toFixed(2)} J travail • {Number(g.absence_days||0).toFixed(2)} J absences • {Number(g.expected_days||0).toFixed(2)} J attendus
                        {Number(g.unjustified_days||0)>0 ? <span className="text-red-600 ml-1">• {Number(g.unjustified_days).toFixed(2)} J non justifies</span> : <span className="text-green-600 ml-1">• Mois complet</span>}
                        <span className="text-slate-400 ml-1">• Facturable: {Number(g.billable_days||0).toFixed(1)}j</span>
                      </p>
                    </div>
                    {isManager && (
                      <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                        <a href={craExportUrl('excel', g.user_id)}><Button variant="ghost" size="sm" className="h-7 text-blue-600"><Download size={13} className="mr-1"/>Excel</Button></a>
                        <a href={craExportUrl('pdf', g.user_id)}><Button variant="ghost" size="sm" className="h-7 text-blue-600"><FilePdf size={13} className="mr-1"/>PDF</Button></a>
                        {g.status === 'submitted' && <Button variant="ghost" size="sm" className="h-7 text-green-600 hover:bg-green-50" onClick={() => handleCRAMonth(g, 'validate')} data-testid={`cra-approve-${g.user_id}`}><Check size={13} weight="bold" className="mr-1" />Valider & signer</Button>}
                        {g.status === 'manager_validated' && <Button variant="ghost" size="sm" className="h-7 text-emerald-700 hover:bg-emerald-50" onClick={() => handleCRAMonth(g, 'close')} data-testid={`cra-close-${g.user_id}`}><Check size={13} weight="bold" className="mr-1" />Cloturer le mois</Button>}
                        {['submitted','manager_validated'].includes(g.status) && <Button variant="ghost" size="sm" className="h-7 text-red-500 hover:bg-red-50" onClick={() => handleCRAMonth(g, 'reject')} data-testid={`cra-reject-${g.user_id}`}><X size={13} weight="bold" className="mr-1" />Rejeter</Button>}
                      </div>
                    )}
                  </div>
                  {expanded && (
                    <div className="border-t bg-slate-50/40 px-3 py-2.5 space-y-3" data-testid={`cra-detail-${g.user_id}`}>
                      {/* Time entries detail */}
                      <div>
                        <p className="text-[11px] font-semibold text-slate-600 mb-1.5">Temps passés ({g.entries.length})</p>
                        {g.entries.length === 0 ? (
                          <p className="text-xs text-slate-400 italic">Aucune saisie</p>
                        ) : (
                          <div className="overflow-x-auto rounded border bg-white">
                            <table className="w-full text-xs">
                              <thead className="bg-slate-100">
                                <tr>
                                  <th className="px-2 py-1.5 text-left font-medium">Date</th>
                                  <th className="px-2 py-1.5 text-left font-medium">Client</th>
                                  <th className="px-2 py-1.5 text-left font-medium">Projet</th>
                                  <th className="px-2 py-1.5 text-left font-medium">Activité</th>
                                  <th className="px-2 py-1.5 text-right font-medium">Jours</th>
                                  <th className="px-2 py-1.5 text-center font-medium">Facturable</th>
                                  <th className="px-2 py-1.5 text-center font-medium">Statut</th>
                                </tr>
                              </thead>
                              <tbody>
                                {g.entries.sort((a,b) => (a.date||'').localeCompare(b.date||'')).map((e, i) => (
                                  <tr key={e.id || i} className="border-t">
                                    <td className="px-2 py-1.5">{e.date ? formatDate(e.date) : '-'}</td>
                                    <td className="px-2 py-1.5 text-slate-700">{e.client_name || <span className="text-slate-300 italic">-</span>}</td>
                                    <td className="px-2 py-1.5">{e.project_name || e.project_code || '-'}</td>
                                    <td className="px-2 py-1.5 text-slate-600">{e.activity || e.description || '-'}</td>
                                    <td className="px-2 py-1.5 text-right font-bold">{e.time_spent}</td>
                                    <td className="px-2 py-1.5 text-center">{e.billable ? '✓' : '—'}</td>
                                    <td className="px-2 py-1.5 text-center"><Badge className={`text-[9px] ${statusBadge[e.status]}`}>{statusLabel[e.status]}</Badge></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                      {/* Absences detail */}
                      <div>
                        <p className="text-[11px] font-semibold text-slate-600 mb-1.5">Absences ({g.absences.length})</p>
                        {g.absences.length === 0 ? (
                          <p className="text-xs text-slate-400 italic">Aucune absence</p>
                        ) : (
                          <div className="overflow-x-auto rounded border bg-white">
                            <table className="w-full text-xs">
                              <thead className="bg-slate-100">
                                <tr>
                                  <th className="px-2 py-1.5 text-left font-medium">Du</th>
                                  <th className="px-2 py-1.5 text-left font-medium">Au</th>
                                  <th className="px-2 py-1.5 text-left font-medium">Type</th>
                                  <th className="px-2 py-1.5 text-right font-medium">Jours</th>
                                  <th className="px-2 py-1.5 text-center font-medium">Statut</th>
                                </tr>
                              </thead>
                              <tbody>
                                {g.absences.map((a, i) => (
                                  <tr key={a.id || i} className="border-t">
                                    <td className="px-2 py-1.5">{a.start_date ? formatDate(a.start_date) : '-'}</td>
                                    <td className="px-2 py-1.5">{a.end_date ? formatDate(a.end_date) : '-'}</td>
                                    <td className="px-2 py-1.5">{a.type || '-'}</td>
                                    <td className="px-2 py-1.5 text-right font-bold">{a.days || '-'}</td>
                                    <td className="px-2 py-1.5 text-center"><Badge className="text-[9px] bg-blue-100 text-blue-700">{a.status || '-'}</Badge></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* EXPENSES TAB */}
        <TabsContent value="expenses">
          {isManager && filteredExp.length > 0 && (
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Button variant="outline" size="sm" onClick={toggleAllExp} data-testid="exp-toggle-all">
                {allExpSelected ? 'Tout désélectionner' : `Tout sélectionner (${allExpKeys.length})`}
              </Button>
              {selectedExp.length > 0 && <>
                <span className="text-xs text-slate-500">{selectedExp.length} sélectionné(s)</span>
                <Button className="btn-primary" size="sm" onClick={() => handleBatchExp('approve')} data-testid="exp-batch-approve"><Check size={14} className="mr-1" />Approuver</Button>
                <Button variant="outline" size="sm" onClick={() => handleBatchExp('revert')} className="text-amber-700 border-amber-300" data-testid="exp-batch-revert"><ArrowCounterClockwise size={14} className="mr-1" />Remettre en attente</Button>
              </>}
            </div>
          )}
          <div className="space-y-2">
            {filteredExp.length === 0 && (
              <Card className="card-surface"><CardContent className="py-8 text-center text-sm text-slate-500">Aucune note de frais pour cette période</CardContent></Card>
            )}
            {filteredExp.map(g => {
              const k = keyOf(g);
              const expanded = expandedExp[k];
              return (
                <Card key={k} className="card-surface" data-testid={`exp-group-${g.user_id}`}>
                  <div className="flex items-center px-3 py-2.5 hover:bg-slate-50 cursor-pointer" onClick={() => setExpandedExp(p => ({ ...p, [k]: !p[k] }))}>
                    {isManager && (
                      <input type="checkbox" className="mr-3" checked={selectedExp.includes(k)} onClick={e => e.stopPropagation()} onChange={() => toggleExp(g)} data-testid={`exp-cb-${g.user_id}`} />
                    )}
                    {expanded ? <CaretDown size={14} className="text-slate-400 mr-2" /> : <CaretRight size={14} className="text-slate-400 mr-2" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-800 truncate">{g.user_name}</span>
                        <Badge className={`text-[10px] ${statusBadge[g.status]}`}>{statusLabel[g.status]}</Badge>
                        <span className="text-[10px] text-slate-400">{g.expenses.length} note(s)</span>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        Total : <b>{g.total.toFixed(2)} €</b> •
                        <span className="text-amber-600 ml-1">{g.pending.toFixed(2)}€ attente</span> /
                        <span className="text-green-600 ml-1">{g.approved.toFixed(2)}€ OK</span>
                        {g.rejected > 0 && <span className="text-red-500 ml-1">/ {g.rejected.toFixed(2)}€ refusé</span>}
                        <span className="text-slate-400 ml-1">• Facturable: {g.billable.toFixed(2)}€</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" className="h-7 text-blue-600 hover:bg-blue-50" onClick={() => downloadExpensePdf(g)} data-testid={`exp-pdf-${g.user_id}`} title="Télécharger PDF">
                        <Download size={13} weight="bold" className="mr-1" />PDF
                      </Button>
                      {isManager && <>
                        {g.status !== 'approved' && <Button variant="ghost" size="sm" className="h-7 text-green-600 hover:bg-green-50" onClick={() => handleExpMonth(g, 'approve')} data-testid={`exp-approve-${g.user_id}`}><Check size={13} weight="bold" className="mr-1" />Approuver</Button>}
                        {g.status !== 'rejected' && <Button variant="ghost" size="sm" className="h-7 text-red-500 hover:bg-red-50" onClick={() => handleExpMonth(g, 'reject')} data-testid={`exp-reject-${g.user_id}`}><X size={13} weight="bold" className="mr-1" />Rejeter</Button>}
                        {g.status !== 'pending' && g.status !== 'empty' && <Button variant="ghost" size="sm" className="h-7 text-amber-600 hover:bg-amber-50" onClick={() => handleExpMonth(g, 'revert')} data-testid={`exp-revert-${g.user_id}`}><ArrowCounterClockwise size={13} weight="bold" className="mr-1" />Réinit.</Button>}
                      </>}
                    </div>
                  </div>
                  {expanded && (
                    <div className="border-t bg-slate-50/40 px-3 py-2.5" data-testid={`exp-detail-${g.user_id}`}>
                      <div className="overflow-x-auto rounded border bg-white">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="px-2 py-1.5 text-left font-medium">Date</th>
                              <th className="px-2 py-1.5 text-left font-medium">Description</th>
                              <th className="px-2 py-1.5 text-left font-medium">Catégorie</th>
                              <th className="px-2 py-1.5 text-left font-medium">Projet</th>
                              <th className="px-2 py-1.5 text-right font-medium">Montant</th>
                              <th className="px-2 py-1.5 text-center font-medium">Facturable</th>
                              <th className="px-2 py-1.5 text-center font-medium">Statut</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.expenses.map((e, i) => (
                              <tr key={e.id || i} className="border-t">
                                <td className="px-2 py-1.5">{e.date ? formatDate(e.date) : '-'}</td>
                                <td className="px-2 py-1.5">{e.description}</td>
                                <td className="px-2 py-1.5">{e.category}</td>
                                <td className="px-2 py-1.5 text-slate-600">{e.project_name || '-'}</td>
                                <td className="px-2 py-1.5 text-right font-bold">{(e.amount || 0).toFixed(2)} €</td>
                                <td className="px-2 py-1.5 text-center">{e.billable ? '✓' : '—'}</td>
                                <td className="px-2 py-1.5 text-center"><Badge className={`text-[9px] ${statusBadge[e.status || 'pending']}`}>{statusLabel[e.status || 'pending']}</Badge></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={rejectDialog.open} onOpenChange={o => !o && setRejectDialog(p => ({ ...p, open: false }))}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Rejeter le mois</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <div className="space-y-1">
              <Label className="text-xs">Motif de rejet (envoyé au collaborateur)</Label>
              <Textarea value={rejectDialog.reason} onChange={e => setRejectDialog(p => ({ ...p, reason: e.target.value }))} rows={3} placeholder="Préciser la raison du rejet..." data-testid="reject-reason" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setRejectDialog(p => ({ ...p, open: false }))}>Annuler</Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white" size="sm" onClick={handleReject} data-testid="reject-confirm">Rejeter et notifier</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
